import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PageTabItem {
  value: string;
  label: string;
  icon?: LucideIcon;
  href?: string;
}

export interface PageTabsProps {
  items: PageTabItem[];
  activeValue: string | null;
  onSelect?: (value: string) => void;
  className?: string;
}

export function PageTabs({ items, activeValue, onSelect, className }: PageTabsProps) {
  return (
    <div
      className={cn(
        "flex gap-5 overflow-x-auto border-b border-[hsl(var(--stone-200))]/60 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = item.value === activeValue;
        const baseClasses = cn(
          "flex items-center gap-1.5 whitespace-nowrap pb-[9px] text-[13px] border-b-2 -mb-px transition-colors",
          isActive
            ? "text-[hsl(var(--ink))] font-medium border-[hsl(var(--brand-ochre))]"
            : "text-muted-foreground border-transparent hover:text-[hsl(var(--ink))]",
        );
        const content = (
          <>
            {Icon && <Icon className="h-[15px] w-[15px]" />}
            <span>{item.label}</span>
          </>
        );
        if (item.href) {
          return (
            <Link key={item.value} to={item.href} className={baseClasses}>
              {content}
            </Link>
          );
        }
        return (
          <button
            key={item.value}
            type="button"
            onClick={() => onSelect?.(item.value)}
            className={baseClasses}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}
